import '../styles/dashboard.css'
/**
 * Arquivo: src/pages/Dashboard.tsx
 * Versão: 5.6.6
 * Data: 02/10/2026
 * Descrição: Dashboard desktop interativo com detalhamento e ações rápidas.
 * Comparativo mensal carregado pela mesma fonte oficial do resumo financeiro.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDownLeft, ArrowLeftRight, ArrowUpRight, CalendarClock,
  ChevronRight, CircleAlert, CreditCard, Equal, ListChecks,
  Plus, WalletCards, TrendingUp,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { obterAcessoBase, obterBaseAtiva, obterResumoDashboard, podeEscrever } from '../services/financeiro'
import type { BaseFinanceira, Lancamento, PerfilAcesso } from '../types/database'
import { usePeriod } from '../context/PeriodContext'
import DashboardDueSummary from '../components/DashboardDueSummary'
import AppSkeleton from '../components/AppSkeleton'

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const dataBR = (v?: string | null) => {
  if (!v) return '—'
  const p = v.slice(0, 10).split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : v
}
type Resumo = Awaited<ReturnType<typeof obterResumoDashboard>>
type LimitePendentes = 5 | 10 | 20

const periodoAnterior = (periodo: string) => {
  if (!/^\d{4}-\d{2}$/.test(periodo)) return null
  const [ano, mes] = periodo.split('-').map(Number)
  const anterior = new Date(Date.UTC(ano, mes - 2, 1, 12))
  return `${anterior.getUTCFullYear()}-${String(anterior.getUTCMonth() + 1).padStart(2, '0')}`
}

const variacaoPercentual = (atual: number, anterior: number) => {
  if (!anterior && !atual) return null
  if (!anterior) return null
  return ((atual - anterior) / Math.abs(anterior)) * 100
}
const textoVariacao = (valor: number | null) => {
  if (valor === null || !Number.isFinite(valor)) return 'Sem base anterior'
  const sinal = valor > 0 ? '+' : ''
  return `${sinal}${valor.toFixed(1).replace('.', ',')}% vs. mês anterior`
}

export default function Dashboard() {
  const { periodo, label } = usePeriod()
  const nav = useNavigate()
  const [base, setBase] = useState<BaseFinanceira | null>(null)
  const [perfil, setPerfil] = useState<PerfilAcesso | null>(null)
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [comparativo, setComparativo] = useState<Resumo | null>(null)
  const [erro, setErro] = useState('')
  const [loading, setLoading] = useState(true)
  const [novoAberto, setNovoAberto] = useState(false)
  const [limitePendentes, setLimitePendentes] = useState<LimitePendentes>(() => {
    const salvo = Number(localStorage.getItem('cf_dashboard_pendentes_limite'))
    return salvo === 5 || salvo === 20 ? salvo : 10
  })

  const cargaAtual = useRef(0)
  const carregar = async () => {
    const carga = ++cargaAtual.current
    setLoading(true)
    setErro('')
    try {
      const b = base || await obterBaseAtiva()
      if (!b) throw new Error('Base Principal não localizada.')
      const anterior = periodoAnterior(periodo)
      const [dados, dadosAnteriores, acesso] = await Promise.all([
        obterResumoDashboard(b.id, periodo),
        anterior ? obterResumoDashboard(b.id, anterior) : Promise.resolve(null),
        obterAcessoBase(b.id),
      ])
      if (carga !== cargaAtual.current) return
      setBase(b)
      setResumo(dados)
      setComparativo(dadosAnteriores)
      setPerfil(acesso?.perfil || null)
    } catch (e) {
      if (carga === cargaAtual.current) setErro(e instanceof Error ? e.message : 'Erro ao carregar Dashboard.')
    } finally {
      if (carga === cargaAtual.current) setLoading(false)
    }
  }

  useEffect(() => { void carregar(); return () => { cargaAtual.current++ } }, [periodo])

  const pendentes: Lancamento[] = useMemo(
    () => (resumo?.recentes || []).slice(0, limitePendentes),
    [resumo, limitePendentes],
  )
  const podeAlterar = podeEscrever(perfil)
  const variacaoEntradas = variacaoPercentual(Number(resumo?.receitasRealizadas || 0), Number(comparativo?.receitasRealizadas || 0))
  const variacaoSaidas = variacaoPercentual(Number(resumo?.despesasRealizadas || 0), Number(comparativo?.despesasRealizadas || 0))
  const variacaoResultado = variacaoPercentual(Number(resumo?.resultadoPeriodo || 0), Number(comparativo?.resultadoPeriodo || 0))
  const totalFluxoRealizado = Number(resumo?.receitasRealizadas || 0) + Number(resumo?.despesasRealizadas || 0)
  const pesoEntradas = totalFluxoRealizado > 0 ? (Number(resumo?.receitasRealizadas || 0) / totalFluxoRealizado) * 100 : 50
  const alterarLimite = (valor: LimitePendentes) => {
    setLimitePendentes(valor)
    localStorage.setItem('cf_dashboard_pendentes_limite', String(valor))
  }
  const goNovo = (tipo: 'despesa' | 'receita' | 'transferencia') => nav(
    tipo === 'transferencia' ? '/transferencias?novo=1' : `/lancamentos?novo=${tipo}`,
  )

  if (loading && !resumo) return <AppSkeleton variant="dashboard" />

  return <section className="ff-dash">
    <div className="ff-dash-heading"><div><span className="ff-dash-eyebrow">VISÃO GERAL · {label}</span><h1>Seu financeiro, com clareza</h1><p>Saldos, movimentos e próximos compromissos em um só lugar.</p></div><span className="ff-dash-preview">Nova identidade FF</span></div>
    {erro && <div className="error-box" role="alert">{erro}</div>}
    <div className="ff-dash-overview">
      <button className="ff-dash-hero" onClick={()=>nav('/lancamentos?visao=resultado')}><span className="ff-dash-eyebrow">RESULTADO REALIZADO</span><strong className={`privacy-value ${(resumo?.resultadoPeriodo||0)<0?'ff-negative':''}`}>{brl.format(resumo?.resultadoPeriodo||0)}</strong><span>Entradas menos saídas realizadas no período</span><small>{textoVariacao(variacaoResultado)} <ChevronRight size={15}/></small></button>
      <div className="ff-dash-balances"><button onClick={()=>nav('/contas')}><span className="ff-dash-icon"><WalletCards size={20}/></span><span>Saldo atual das contas</span><strong className="privacy-value">{brl.format(resumo?.saldoDisponivel||0)}</strong><small>Consultar contas <ChevronRight size={14}/></small></button><button onClick={()=>nav('/lancamentos?status=PENDENTE')}><span className="ff-dash-icon"><TrendingUp size={20}/></span><span>Saldo projetado</span><strong className="privacy-value">{brl.format(resumo?.saldoFinalProjetado||0)}</strong><small>Saldo atual + entradas previstas − saídas previstas</small></button></div>
    </div>
    <nav className="ff-dash-shortcuts" aria-label="Ações rápidas"><button disabled={!podeAlterar} onClick={()=>goNovo('despesa')}><span className="ff-dash-icon ff-expense"><ArrowUpRight size={20}/></span>Nova saída<Plus size={15}/></button><button disabled={!podeAlterar} onClick={()=>goNovo('receita')}><span className="ff-dash-icon ff-income"><ArrowDownLeft size={20}/></span>Nova entrada<Plus size={15}/></button><button disabled={!podeAlterar} onClick={()=>goNovo('transferencia')}><span className="ff-dash-icon"><ArrowLeftRight size={20}/></span>Transferir<Plus size={15}/></button><button onClick={()=>nav('/cartoes')}><span className="ff-dash-icon ff-credit"><CreditCard size={20}/></span>Cartões<ChevronRight size={15}/></button></nav>
    <div className="ff-dash-section"><h2>Movimentação do período</h2><span>Realizado e previsto</span></div>
    <div className="ff-dash-movements">
      <button onClick={()=>nav('/receber')}><span className="ff-dash-card-title"><span className="ff-dash-icon ff-income"><ArrowDownLeft size={20}/></span>Entradas<ChevronRight size={16}/></span><small>Realizadas</small><strong className="privacy-value ff-income">{brl.format(resumo?.receitasRealizadas||0)}</strong><span className="ff-dash-card-foot">A receber <b className="privacy-value">{brl.format(resumo?.receitasAReceber||0)}</b></span></button>
      <button onClick={()=>nav('/pagar')}><span className="ff-dash-card-title"><span className="ff-dash-icon ff-expense"><ArrowUpRight size={20}/></span>Saídas<ChevronRight size={16}/></span><small>Realizadas</small><strong className="privacy-value ff-expense">{brl.format(resumo?.despesasRealizadas||0)}</strong><span className="ff-dash-card-foot">A pagar <b className="privacy-value">{brl.format(resumo?.despesasAPagar||0)}</b></span></button>
      <button onClick={()=>nav('/transferencias')}><span className="ff-dash-card-title"><span className="ff-dash-icon"><ArrowLeftRight size={20}/></span>Transferências<ChevronRight size={16}/></span><small>Realizadas</small><strong className="privacy-value">{brl.format(resumo?.transferenciasRealizadas||0)}</strong><span className="ff-dash-card-foot">Entre contas e investimentos</span></button>
    </div>
    <div className="ff-dash-section"><h2>Agenda financeira</h2><span>Prioridade por vencimento</span></div>
    <div className="ff-dash-agenda">{[{title:'Atrasadas',value:resumo?.atrasadas||0,group:'atrasadas',Icon:CircleAlert},{title:'Vencem hoje',value:resumo?.hojeValor||0,group:'hoje',Icon:CalendarClock},{title:'Próximos 7 dias',value:resumo?.proximos7||0,group:'7dias',Icon:CalendarClock}].map(({title,value,group,Icon})=><button key={group} onClick={()=>nav(`/lancamentos?grupo=${group}`)}><Icon size={19}/><span>{title}<strong className="privacy-value">{brl.format(value)}</strong></span><ChevronRight size={16}/></button>)}</div>
    <section className="ff-dash-pending"><div className="ff-dash-pending-head"><div><h2>Próximos a conferir</h2><p>Lançamentos previstos, dos vencimentos mais antigos aos mais recentes.</p></div><label>Mostrar<select value={limitePendentes} onChange={e=>alterarLimite(Number(e.target.value) as LimitePendentes)} aria-label="Quantidade de lançamentos previstos"><option value={5}>5</option><option value={10}>10</option><option value={20}>20</option></select></label></div>
      <div className="ff-dash-entry-list">{pendentes.map(l=><button key={l.id} onClick={()=>nav(`/lancamentos?acoes=${l.id}`)}><span className={`ff-dash-icon ${l.tipo==='RECEITA'?'ff-income':'ff-expense'}`}>{l.tipo==='RECEITA'?<ArrowDownLeft size={19}/>:<ArrowUpRight size={19}/>}</span><span className="ff-dash-entry-main"><strong>{l.descricao}</strong><small>{dataBR(l.vencimento)} · {l.tipo==='RECEITA'?'Entrada':'Saída'} · Previsto</small></span><b className={`privacy-value ${l.tipo==='RECEITA'?'ff-income':'ff-expense'}`}>{brl.format(Number(l.valor_previsto||0))}</b><ChevronRight size={15}/></button>)}{!pendentes.length&&<p className="ff-dash-empty">Nenhum lançamento previsto no período.</p>}</div><button className="ff-dash-all" onClick={()=>nav('/lancamentos?status=PENDENTE')}>Ver todos os previstos <ChevronRight size={15}/></button>
    </section>
  </section>
}
